/**
 * Small pieces shared by the CLIENT side of Get it built (the landing page,
 * Post a request, the request, the quote and placing the order).
 * Doc: docs/marketplace_get_it_built.md
 *
 * Nothing here holds a business number: shares, fees and prices always come
 * from the API (the request, the quote or the public terms).
 */
import type { BuildTerms } from '@/api/marketplace'
import { money } from '@/api/marketplace'
import type { BuildRequest, Monthly, Quote, QuoteStatus } from '@/api/marketplaceBuild'

export type Tone = '' | 'ok' | 'info' | 'warn' | 'bad'

/** A round avatar with the builder's (or person's) initials. */
export function Avatar({ text, size = 40 }: { text: string; size?: number }) {
  return (
    <span aria-hidden="true" className="mkt-tile" style={{
      width: size, height: size, borderRadius: 999, background: 'var(--color-accent)',
      fontSize: Math.max(13, Math.round(size * 0.34)),
    }}>{(text || '?').slice(0, 2).toUpperCase()}</span>
  )
}

/** Whether a monthly price was quoted at all. */
export function hasMonthly(m: Monthly | Record<string, never> | null | undefined): m is Monthly {
  return !!m && typeof (m as Monthly).price_minor === 'number' && (m as Monthly).price_minor > 0
}

/** The whole monthly amount, when it can be known (per server, or per person with a head count). */
export function monthlyTotal(m: Monthly | Record<string, never> | null | undefined): number | null {
  if (!hasMonthly(m)) return null
  if (m.basis === 'server') return m.price_minor
  return m.seats ? m.price_minor * m.seats : null
}

/** "₹240 per person a month (₹14,400 for 60 people)" / "₹9,000 a month (size M)". */
export function monthlyText(m: Monthly | Record<string, never> | null | undefined, currency: string): string {
  if (!hasMonthly(m)) return 'Not quoted yet'
  if (m.basis === 'server') return `${money(m.price_minor, currency)} a month${m.size ? ` (server size ${m.size})` : ''}`
  const each = `${money(m.price_minor, currency)} per person a month`
  return m.seats ? `${each} (${money(m.price_minor * m.seats, currency)} for ${m.seats} ${m.seats === 1 ? 'person' : 'people'})` : each
}

/** Short form for cards: "₹14,400 a month" or "₹240 per person a month". */
export function monthlyShort(m: Monthly | Record<string, never> | null | undefined, currency: string): string {
  if (!hasMonthly(m)) return 'monthly price not quoted'
  const total = monthlyTotal(m)
  if (total !== null) return `${money(total, currency)} a month`
  return `${money(m.price_minor, currency)} per person a month`
}

/** Build price + twelve months of use, when the monthly total is known. */
export function firstYear(q: Pick<Quote, 'price_minor' | 'monthly'>): number | null {
  const t = monthlyTotal(q.monthly)
  return t === null ? null : q.price_minor + 12 * t
}

/** Every brief item, in order (coverage is keyed by the item's text). */
export function briefItems(req: Pick<BuildRequest, 'brief'> | null | undefined): string[] {
  return (req?.brief || []).flatMap((s) => s.items || [])
}

/** An item a builder did not mark is counted as included (same rule as the server). */
export function covers(q: Pick<Quote, 'coverage'>, item: string): { included: boolean; note: string } {
  const c = q.coverage?.[item]
  return { included: c ? c.included !== false : true, note: c?.note || '' }
}

export function coveredCount(q: Pick<Quote, 'coverage'>, items: string[]): number {
  return items.filter((i) => covers(q, i).included).length
}

export function weeksText(w: number | null | undefined): string {
  if (!w) return 'Time not given'
  return `${w} ${w === 1 ? 'week' : 'weeks'}`
}

export function quoteStatus(q: Pick<Quote, 'status' | 'expired'>): { label: string; tone: Tone } {
  const map: Record<QuoteStatus, { label: string; tone: Tone }> = {
    draft: { label: 'Not sent yet', tone: '' },
    sent: { label: 'Waiting for your decision', tone: 'info' },
    revision_asked: { label: 'Revision asked', tone: 'warn' },
    superseded: { label: 'Replaced by a newer version', tone: '' },
    declined: { label: 'Declined', tone: 'bad' },
    withdrawn: { label: 'Withdrawn by the builder', tone: 'bad' },
    accepted: { label: 'Ordered', tone: 'ok' },
    not_chosen: { label: 'Not chosen', tone: '' },
  }
  if (q.expired && (q.status === 'sent' || q.status === 'revision_asked')) return { label: 'Expired', tone: 'bad' }
  return map[q.status] || { label: q.status, tone: '' }
}

/** Is the estimate still open to order? */
export function quoteLive(q: Pick<Quote, 'status' | 'expired'>): boolean {
  return (q.status === 'sent' || q.status === 'revision_asked') && !q.expired
}

export function daysLeft(iso: string | null | undefined): number | null {
  if (!iso) return null
  const ms = new Date(iso).getTime() - Date.now()
  return Math.max(0, Math.ceil(ms / 86_400_000))
}

export function requestStatus(r: Pick<BuildRequest, 'status' | 'closes_at' | 'estimates_count'>): { label: string; tone: Tone } {
  if (r.status === 'draft') return { label: 'Draft', tone: '' }
  if (r.status === 'ordered') return { label: 'Ordered', tone: 'ok' }
  if (r.status === 'closed') return { label: 'Closed', tone: '' }
  const d = daysLeft(r.closes_at)
  if (d === null) return { label: 'Open', tone: 'ok' }
  return { label: d === 0 ? 'Open · closes today' : `Open · ${d} ${d === 1 ? 'day' : 'days'} left`, tone: 'ok' }
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
}

/** "10% advance when you order, then 10% for Specs & scope, 30% for …" — from the terms. */
export function termsSentence(terms: BuildTerms): string {
  const [adv, ...rest] = terms.stages
  if (!adv) return ''
  const parts = rest.map((s, i) => `${i === rest.length - 1 && rest.length > 1 ? 'and ' : ''}${s.pct}% for ${s.name.toLowerCase()}`)
  return `a ${adv.pct}% advance when you order, then ${parts.join(', ')}`
}

/** When a stage's share is taken from the client's wallet. */
export function collectText(terms: BuildTerms): string {
  return terms.collect_on === 'approve'
    ? 'Each share is taken when you approve that stage'
    : 'Each share is taken when the builder submits that stage'
}

/** What happens when the client does not answer a stage in time. */
export function noReplyText(terms: BuildTerms): string {
  const r = terms.reminder_days_before?.length || 0
  const reminders = r ? `${r === 1 ? 'a reminder' : `${r} reminders`}, then ` : ''
  return terms.on_no_response === 'support'
    ? `No reply within ${terms.approve_days} days: ${reminders}Twasta support steps in.`
    : `No reply within ${terms.approve_days} days: ${reminders}it is approved automatically.`
}

/** The symbol in front of an amount input ("₹", "$", or the code). */
export function currencySymbol(currency: string): string {
  try {
    const part = new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', { style: 'currency', currency })
      .formatToParts(0).find((p) => p.type === 'currency')
    return part?.value || currency
  } catch {
    return currency
  }
}

/** Typed amount ("1,50,000") → minor units. */
export function parseMajorToMinor(text: string): number {
  const n = Number(String(text).replace(/[^\d.]/g, ''))
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}

export function minorToMajorText(minor: number | null | undefined): string {
  if (!minor) return ''
  const v = minor / 100
  return Number.isInteger(v) ? String(v) : v.toFixed(2)
}

/**
 * A worked example for showing the standard terms before there is a real
 * price: five times the smallest order the terms allow (its smallest stage at
 * the minimum stage amount). Derived from the terms, so it follows any change
 * the instance admin makes; it is only an illustration, never a rule.
 */
export function exampleAmount(terms: BuildTerms, currency: string): number {
  const floor = terms.min_stage_minor?.[currency] || 0
  const minPct = Math.min(...terms.stages.map((s) => s.pct).filter((p) => p > 0))
  if (!floor || !Number.isFinite(minPct) || minPct <= 0) return 0
  const smallest = Math.ceil((floor * 100) / minPct)
  const unit = currency === 'INR' ? 1_000_000 : 10_000 // round to ₹10,000 / $100 so it reads as an estimate
  return Math.ceil((smallest * 5) / unit) * unit
}

export function Loading({ text = 'Loading…' }: { text?: string }) {
  return <div className="mkt-wrap" style={{ padding: '48px 20px' }} role="status"><span className="mkt-muted">{text}</span></div>
}

export function ErrorPanel({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="mkt-wrap" style={{ padding: '32px 20px' }}>
      <div className="mkt-banner bad" role="alert">
        <div><strong>That didn’t load</strong><span>{message}</span></div>
        {onRetry && <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>Try again</button>}
      </div>
    </div>
  )
}
